import { AreaAssessments } from "@/services/backend/gfs/assessment/components/area-assessments";
import { SiteSelection } from "@/services/backend/gfs/assessment/components/site-selection";
import { useAssessmentEditorContext } from "@/services/backend/gfs/assessment/components/assessment-editor-context";
import { useBuildingsByAddressListQuery } from "@/services/backend/buildings/api/building-by-address-api";
import {
  GfsAreaAssessment,
  GfsAreaSnapshot,
  GfsAssessment,
} from "@/services/backend/gfs/assessment/types";
import { GfsCatalogItem } from "@/services/backend/gfs/catalog/types";

export function GfsAssessmentAreasPage() {
  const {
    assessment,
    draft,
    areaScope,
    areaPersistenceStates,
    catalogItems,
    catalogConsumables,
    setAreaScope,
    updateAssessment,
    persistCurrentBuildings,
  } = useAssessmentEditorContext();
  const { data: customerBuildingGroups } = useBuildingsByAddressListQuery({
    customer: { active: true, values: [assessment.customerId] },
  });
  const customerBuildings = (customerBuildingGroups?.items ?? []).flatMap(
    (group) => group.buildings,
  );

  return (
    <div className="space-y-5">
      <SiteSelection
        assessmentId={assessment.id}
        buildings={draft.buildings}
        areaAssessments={draft.areaAssessments ?? []}
        onChange={(nextBuildings) =>
          updateAssessment(
            { buildings: nextBuildings },
            { markPersistence: false },
          )
        }
        onAreaSelect={(area: GfsAreaSnapshot) => {
          if (draft.areaAssessments?.some((item) => item.areaId === area.id)) {
            return;
          }
          updateAssessment(
            {
              areaAssessments: [
                ...(draft.areaAssessments ?? []),
                {
                  areaId: area.id,
                  pestCategories: [],
                  infestationLevel: "",
                  laborDuration: 0,
                  areaSizeM2: 0,
                  finding: "",
                  measures: [],
                  monitors: [],
                  materials: [],
                },
              ],
            },
            { areaId: area.id },
          );
        }}
        onPersist={persistCurrentBuildings}
        onScopeChange={setAreaScope}
        customerBuildings={customerBuildings}
      />
      <AreaAssessments
        buildings={draft.buildings}
        scope={areaScope}
        areaAssessments={draft.areaAssessments ?? []}
        onChange={(nextAreaAssessments, areaId) =>
          updateAssessment({ areaAssessments: nextAreaAssessments }, { areaId })
        }
        onRemove={(areaId) =>
          updateAssessment(
            (current) => {
              const removedArea = current.areaAssessments.find(
                (areaAssessment) => areaAssessment.areaId === areaId,
              );
              return {
                areaAssessments: current.areaAssessments.filter(
                  (areaAssessment) => areaAssessment.areaId !== areaId,
                ),
                materials: removeAreaMaterials(
                  current.materials,
                  removedArea,
                  catalogItems,
                ),
              };
            },
            { areaId },
          )
        }
        persistenceStates={areaPersistenceStates}
        catalogItems={catalogItems}
        catalogConsumables={catalogConsumables}
        onMonitorSelected={(item) =>
          updateAssessment((current) => {
            const materials = [...(current.materials ?? [])];
            item.consumables
              .filter((link) => link.mode === "REFERENCED")
              .forEach((link) => {
                const catalogConsumable = catalogConsumables.find(
                  (consumable) => consumable.id === link.consumableId,
                );
                if (!catalogConsumable) return;
                const materialIndex = materials.findIndex(
                  (material) => material.sourceId === catalogConsumable.id,
                );
                if (materialIndex >= 0) {
                  materials[materialIndex] = {
                    ...materials[materialIndex],
                    quantity: materials[materialIndex].quantity + link.quantity,
                  };
                  return;
                }
                materials.push({
                  sourceId: catalogConsumable.id,
                  key: catalogConsumable.id,
                  name: catalogConsumable.name,
                  unit: catalogConsumable.unit,
                  quantity: link.quantity,
                  unitPrice:
                    link.unitPriceOverride ?? catalogConsumable.unitPrice,
                  takenAt: new Date().toISOString(),
                });
              });
            return { materials };
          })
        }
      />
    </div>
  );
}

function removeAreaMaterials(
  materials: GfsAssessment["materials"],
  areaAssessment: GfsAreaAssessment | undefined,
  catalogItems: GfsCatalogItem[],
): GfsAssessment["materials"] {
  if (!areaAssessment) return materials;

  const catalogItemsById = new Map(
    catalogItems.map((catalogItem) => [catalogItem.id, catalogItem]),
  );
  const quantitiesByConsumableId = new Map<string, number>();

  areaAssessment.monitors.forEach((monitor) => {
    const catalogItem = catalogItemsById.get(monitor.catalogItemId);
    catalogItem?.consumables
      .filter((link) => link.mode === "REFERENCED")
      .forEach((link) => {
        quantitiesByConsumableId.set(
          link.consumableId,
          (quantitiesByConsumableId.get(link.consumableId) ?? 0) +
            link.quantity * monitor.amount,
        );
      });
  });

  return materials.flatMap((material) => {
    const consumableId = material.sourceId ?? material.key;
    const quantityToRemove = quantitiesByConsumableId.get(consumableId);
    if (!quantityToRemove) return [material];

    const remainingQuantity = material.quantity - quantityToRemove;
    return remainingQuantity > 0
      ? [{ ...material, quantity: remainingQuantity }]
      : [];
  });
}
